import { getPurchaseOrderEditingTranslator } from "./purchaseOrderEditingMessages";
import { activePurchaseSuppliers } from "../../utils/purchaseSuppliers";
import { itemMatchesSupplier, itemSupplierMatches } from "../../utils/supplierIdentity";
import { localDate } from "../../utils/tenderManagement";
import { getLocationName } from "../../utils/locationNames";
import { canApplyPurchaseSavePrice, purchasePriceSaveContext, samePurchaseSaveLine } from "../../utils/purchasePriceSaveGuard";
/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 12/10/2025
 * Todos los derechos reservados / All rights reserved.
 */

import { useInlineDestinations, createDestinationLineKey, useDestinationSaveQueue } from "./ReceivingDestinations";
import React, { useState, useEffect, useRef } from "react";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { getDocumentUnitName } from "../../utils/unitNames";
import { purchaseSupplierError, supplierMinimumLabel, getDocumentTranslator, documentStatus } from "./documentPopupMessages";
import "../../PurchasingUi.css";
import "./PurchasingDocumentPopups.css";
import { useDocumentDialog } from "./PurchasingDocumentDialog";
import axios from "../../../api/axiosInstance";
import { readStoredUser } from "../../../utils/sessionUser";
import useIsMobile from "../../../hooks/useIsMobile";
import AddItemPopup from "./AddItemPopup"; // 🆕 abrir creación de artículos desde la PO
import { TenderPriceLabel, purchasePriceSnapshot, usePurchaseTenderPrices, clearedPurchasePrice } from "./TenderPriceFeedback";
import usePurchasePricePreviews, { PURCHASE_PRICE_PAGE_SIZE } from "../../hooks/usePurchasePricePreviews";
import PurchasePricePreview, { PurchasePricePagination, purchasePriceText } from "../PurchasePricePreview";

export default function AddOrderPopup({ isOpen, onClose, onAdded, onDeleted }) {
  const latestFormRef = useRef(null);
  const enqueueDestinationSave = useDestinationSaveQueue();
  const { themeId, languageId } = usePmsTheme();
  const t = getDocumentTranslator(languageId);
  const editText = getPurchaseOrderEditingTranslator(languageId);
  const { confirm: confirmDocument, notify: notifyDocument, dialog } = useDocumentDialog();
  const isMobile = useIsMobile();
  const [form, setForm] = useState({
    order_code: "",
    type: "purchase",
    supplier_id: "",
    order_date: localDate(),
    expected_delivery_date: "",
    destination_location_id: "",
    notes: "",
    lines: [],
  });

  

  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [inventoryLocations, setInventoryLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showItemPopup, setShowItemPopup] = useState(false);
  const [pendingLineIndex, setPendingLineIndex] = useState(null);
  const [itemPicker, setItemPicker] = useState({ lineIndex: null, query: "" });
  const [itemPickerPage, setItemPickerPage] = useState(1);
  const [supplierPicker, setSupplierPicker] = useState({ open: false, query: "" });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingDraft, setDeletingDraft] = useState(false);
  const linesSectionRef = useRef(null);
  const tableWrapRef = useRef(null);

  const currentUser = readStoredUser();
  const canCreateOrder = Number(currentUser?.id) === 1 || currentUser?.permissions?.["purchasing.orders.create"] === true;
  const canDeleteOrder = Number(currentUser?.id) === 1 || currentUser?.permissions?.["purchasing.orders.delete"] === true;
  const businessUnitId = currentUser?.active_business_unit_id;
  usePurchaseTenderPrices({ form, setForm, documentType: "order", enabled: isOpen && canCreateOrder && (!form.status || form.status === "draft"), businessUnitId });
  

  // 🟢 Cargar datos y crear draft al abrir el popup
  useEffect(() => {
    if (!isOpen || !canCreateOrder) return;

    const initializePopup = async () => {
      try {
        // 1️⃣ Cargar catálogos base
        const [supRes, itemRes, unitRes, locationsRes] = await Promise.all([
          axios.get(`/api/purchasing/suppliers/options?business_unit_id=${businessUnitId}`),
          axios.get(`/api/purchasing/items`),
          axios.get(`/api/purchasing/units?business_unit_id=${businessUnitId}`),
          axios.get("/api/purchasing/inventory/locations?include_inactive=false"),
        ]);

        setSuppliers(activePurchaseSuppliers(supRes.data));
        setItems(itemRes.data || []);
        setUnits(unitRes.data || []);
        setInventoryLocations(Array.isArray(locationsRes.data) ? locationsRes.data : []);

        // 2️⃣ Crear borrador (solo si no existe)
        let draft = null;
        if (!form.id) {
          const draftDate = localDate();
          const draftRes = await axios.post(`/api/purchasing/orders`, {
            order_date: draftDate,
            type: form.type,
            supplier_id: form.supplier_id || null,
          });
          draft = draftRes.data;
          console.log("🟢 [AddOrderPopup] Created draft:", draft.order_code);
          setForm((prev) => ({
            ...prev,
            id: draft.id ?? prev.id,
            order_code: draft.order_code ?? prev.order_code,
            order_date: draft.order_date ? String(draft.order_date).slice(0, 10) : draftDate,
            status: draft.status ?? prev.status,
            destination_location_id: draft.destination_location_id ? String(draft.destination_location_id) : "",
          }));


        }

        console.log("[AddOrderPopup] Loaded data:", {
          suppliers: supRes.data.length,
          items: itemRes.data.length,
          units: unitRes.data.length,
          draft: draft?.order_code || "(existing)",
        });
      } catch (err) {
        console.error("❌ [AddOrderPopup] Error initializing popup:", err);
      }
    };

    initializePopup();
  }, [isOpen, canCreateOrder]);

  useEffect(() => {
    if (itemPicker.lineIndex === null) return;
    if (itemPicker.lineIndex >= (form.lines || []).length) {
      setItemPicker({ lineIndex: null, query: "" });
    }
  }, [form.lines, itemPicker.lineIndex]);

  
  // 🧠 AutoSave total general (detecta también deletes y cambios internos)
  useEffect(() => {
    if (!isOpen || !canCreateOrder || !form.id) return;
    if (!Array.isArray(form.lines)) return;

    const total = form.lines.reduce((sum, line) => {
      const q = parseFloat(line.quantity || 0);
      const p = parseFloat(line.unit_price || 0);
      return sum + q * p;
    }, 0);

    if (isNaN(total)) return;

    // 🕐 debounce corto para evitar spam de PATCHs
    const timeout = setTimeout(async () => {
      try {
        await axios.patch(`/api/purchasing/orders/${form.id}`, { total_price: total });
        console.log(`[PO][AutoSave][Total] total_price → ${total.toFixed(2)}`);
      } catch (err) {
        console.error("❌ [PO][AutoSave][Total] Error saving total_price:", err);
      }
    }, 250);

    return () => clearTimeout(timeout);

    // 🔁 JSON.stringify → detecta cambios profundos (qty, price, delete, add)
      }, [JSON.stringify(form.lines), isOpen, canCreateOrder]);



  const renderLineLocation = useInlineDestinations({
    lines: form?.lines || [], items, locations: inventoryLocations,
    headerLocationId: form?.destination_location_id, scopeKey: form.id, disabled: !isOpen || !canCreateOrder,
    onChange: (index, destination, source) => {
      const line = form.lines[index];
      if (!line) return;
      const nextLine = { ...line, destination_location_id: destination, destination_location_source: source };
      setForm((previous) => ({ ...previous, lines: previous.lines.map((entry, i) => i === index ? nextLine : entry) }));
      autoSaveLine(index, nextLine);
    },
  });

        const handleChange = (e) => {
          const { name, value } = e.target;

          console.log(
            `[PO][handleChange] name=${name}, value=${value}, current expected_delivery_date=${form.expected_delivery_date}`
          );

          setForm((prev) => ({ ...prev, [name]: value }));

          // 🧠 Delay controlado para autosave
          if (form.id) {
            clearTimeout(window.__poAutosaveTimer);
            window.__poAutosaveTimer = setTimeout(() => {
              console.log(
                `[PO][AutoSaveTrigger] name=${name}, value=${value}, form.expected_delivery_date=${form.expected_delivery_date}`
              );
              autoSaveField(name, value);
            }, 400);
          }
        };







    // 🧠 AutoSave individual (igual que Reservations Draft)
    const autoSaveField = async (field, value) => {
      try {
        if (!canCreateOrder || !form.id) return;

        console.log("[PO][AutoSave] PATCH →", `/api/purchasing/orders/${form.id}`, {
          [field]: value,
        });

        await axios.patch(`/api/purchasing/orders/${form.id}`, { [field]: value });

        console.log(`[PO][AutoSave] ${field} → ${value} ✅`);
      } catch (err) {
        console.error("❌ [PO][AutoSave] Error saving", field, ":", err);
        if (purchaseSupplierError(err, languageId)) setError(purchaseSupplierError(err, languageId));
      }
    };




  const addLine = () => {
    if (!form.supplier_id) return;

    setForm((prev) => ({
      ...prev,
      lines: [
        {
          _destinationKey: createDestinationLineKey(),
          item_id: "",
          quantity: "",
          unit_id: "",
          pack_size: 1,
          subunit_id: "",
          unit_price: "",
          supplier_id: form.supplier_id,

        },
        ...prev.lines,
      ],
    }));

    window.setTimeout(() => {
      tableWrapRef.current?.scrollTo({ top: 0, left: 0, behavior: "smooth" });

      const modalNode = linesSectionRef.current?.closest(".opq-modal");
      if (modalNode && linesSectionRef.current) {
        modalNode.scrollTo({
          top: Math.max(linesSectionRef.current.offsetTop - 18, 0),
          behavior: "smooth",
        });
      }

      setItemPicker({ lineIndex: 0, query: "" });
    }, 0);
  };

  const removeLine = async (index) => {
  const line = form.lines[index];
  console.log("[PO][DeleteLine] Trying to delete:", line);

  if (!(await confirmDocument(t("Delete this line?")))) return;

  try {
    if (line.id) {
      console.log(`[PO][DeleteLine] Sending DELETE for line.id=${line.id}`);
      const res = await axios.delete(`/api/purchasing/orders/order-lines/${line.id}`);
      console.log(`[PO][DeleteLine] Backend response:`, res.data);
    } else {
      console.warn("[PO][DeleteLine] No line.id found!");
    }

    setForm((prev) => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== index),
    }));
    setItemPicker({ lineIndex: null, query: "" });
  } catch (err) {
    console.error("❌ [PO][DeleteLine] Error:", err);
    await notifyDocument(t("Error deleting order line."));
  }
};



  const updateLine = (index, changes) => {
    setForm((prev) => {
      const line = prev.lines[index];
      if (!line) return prev;
      const updatedLine = { ...line, ...changes };
      if ("item_id" in changes && Number(line.item_id) !== Number(changes.item_id)) {
        Object.assign(updatedLine, clearedPurchasePrice());
        updatedLine.destination_location_id = null;
        updatedLine.destination_location_source = null;
      }
      return {
        ...prev,
        lines: prev.lines.map((entry, i) => i === index ? updatedLine : entry),
      };
    });
  };

  const handleLineChange = (index, field, value) => {
    updateLine(index, { [field]: value });
  };

    // 🧠 AutoSave por línea (draft activo)
    latestFormRef.current = form;
    const autoSaveLine = (index, overrideLine) => {
      const line = overrideLine || latestFormRef.current?.lines[index];
      if (!line) return Promise.resolve();
      const key = line._destinationKey || line.id || `item-${line.item_id}`;
      return enqueueDestinationSave(key, () => {
        const currentIndex = (latestFormRef.current?.lines || []).findIndex((entry) => (entry._destinationKey || entry.id || `item-${entry.item_id}`) === key);
        if (currentIndex < 0) return null;
        return persistOrderLine(currentIndex, overrideLine);
      });
    };
    const persistOrderLine = async (index, overrideLine) => {
      try {
        if (!canCreateOrder) return;
        const savingForm = latestFormRef.current;
        if (!savingForm?.id) return;
        const line = overrideLine || savingForm.lines[index];

        // ⚠️ No guardar líneas vacías
        if (!line?.item_id || !line.quantity) return;
        const saveContext = purchasePriceSaveContext(savingForm, line, "order");

        const payload = {
          order_id: savingForm.id,
          ...line,
          supplier_id: savingForm.supplier_id,
          destination_location_id: line.destination_location_id || savingForm.destination_location_id || null,
          total_price:
            line.quantity && line.unit_price
              ? parseFloat(line.quantity) * parseFloat(line.unit_price)
              : 0,
        };

        const res = await axios.post(`/api/purchasing/orders/order-lines/autosave`, payload);
        console.log(`[PO][AutoSaveLine] Line ${index + 1} saved`, res.data);

        // 🟢 Guardar el id devuelto en la línea correspondiente
        if (res.data?.id) {
          setForm((prev) => {
            const savedIndex = (prev?.lines || []).findIndex((entry) => samePurchaseSaveLine(prev, entry, saveContext));
            if (savedIndex < 0) return prev;
            const newLines = [...prev.lines];
            const currentLine = newLines[savedIndex];
            const acceptPrice = canApplyPurchaseSavePrice(prev, currentLine, saveContext, "order", res.data);
            const price = res.data.unit_price ?? currentLine.unit_price;
            newLines[savedIndex] = {
              ...currentLine,
              id: res.data.id,
              ...(!currentLine.destination_location_id && res.data.destination_location_id ? { destination_location_source: currentLine.destination_location_source || "suggested" } : {}),
              destination_location_id: currentLine.destination_location_id || res.data.destination_location_id || null,
              ...(acceptPrice ? { ...purchasePriceSnapshot(res.data), unit_price: price, total_price: Number(currentLine.quantity || 0) * Number(price || 0) } : {}),
            };
            return { ...prev, lines: newLines };
          });
        }
      } catch (err) {
        console.error(`❌ [PO][AutoSaveLine] Error saving line ${index + 1}:`, err);
        if (purchaseSupplierError(err, languageId)) setError(purchaseSupplierError(err, languageId));
      }
    };



    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!canCreateOrder) return;
      setSaving(true);
      setError("");

      try {
        if (!form.id) {
          setError(t("No draft found for this order. Please reopen the popup."));
          return;
        }

        // 🟢 LINKED MODE
        const res = await axios.patch(`/api/purchasing/orders/${form.id}`, {
          type: form.type,
          supplier_id: form.supplier_id || null,
          order_date: form.order_date,
          expected_delivery_date: form.expected_delivery_date,
          destination_location_id: form.destination_location_id || null,
          notes: form.notes,
          status: "draft",
        });
          console.log("✅ [AddOrderPopup] Linked order saved as draft:", res.data);

          if (typeof onAdded === "function") onAdded(res.data);

          resetForm();
          onClose();
      } catch (err) {
        console.error("❌ [AddOrderPopup] Error saving draft:", err);
        setError(purchaseSupplierError(err, languageId) || err?.response?.data?.error || t("Error saving order."));
      } finally {
        setSaving(false);
      }
    };



  // 🔹 Crear nuevo item desde una línea
  const handleCreateItem = (lineIndex) => {
      setPendingLineIndex(lineIndex);
      setShowItemPopup(true);
    };

    const handleItemAdded = async (newItem) => {
      try {
        // 🟦 1️⃣ Releer el item recién creado con suppliers incluidos
        const refreshed = await axios.get(`/api/purchasing/items/${newItem.id}`);
        const enrichedItem = refreshed.data;

        if (form.supplier_id && !enrichedItem.item_suppliers?.length) {
          enrichedItem.item_suppliers = [{ supplier_id: parseInt(form.supplier_id) }];
        }

        // 🟢 Añadir el item enriquecido a la lista global
        setItems((prev) => [enrichedItem, ...prev]);


        // 🟢 3️⃣ Completar datos comunes de la línea
        if (pendingLineIndex !== null) {
          applyItemToLine(pendingLineIndex, enrichedItem);
          setPendingLineIndex(null);
        }
      } catch (err) {
        console.error("❌ [PO][handleItemAdded] Error reloading item:", err);
      }
    };


  // 🧮 Calcular total general de todas las líneas
  const calculateOrderTotal = () => {
    return form.lines.reduce((sum, line) => {
      const q = parseFloat(line.quantity || 0);
      const p = parseFloat(line.unit_price || 0);
      return sum + q * p;
    }, 0);
  };

  const getPackagingLabel = (line) => {
    const unit = getDocumentUnitName(line.unit, line.unit_id, units, languageId);
    const subunit = getDocumentUnitName(line.subunit, line.subunit_id, units, languageId);
    const pack = Number(line.pack_size || 1);

    if (!unit && !subunit) return "—";
    if (pack > 1) {
      return `${unit || "?"} (${pack} x ${subunit || unit})`;
    }
    return unit || subunit || "—";
  };

  const getLineTotal = (line) => {
    const quantity = parseFloat(line.quantity || 0);
    const unitPrice = parseFloat(line.unit_price || 0);
    return quantity && unitPrice ? (quantity * unitPrice).toFixed(2) : "—";
  };

  const getSelectedItemLabel = (line) => {
    const selectedItem = items.find((it) => String(it.id) === String(line.item_id));
    if (!selectedItem) return t("Select item");
    return selectedItem.code ? `${selectedItem.code} ${selectedItem.name}` : selectedItem.name;
  };

  const getSelectedSupplierLabel = () => {
    const selectedSupplier = suppliers.find(
      (supplier) => String(supplier.id) === String(form.supplier_id || "")
    );
    if (!selectedSupplier) return t("Select supplier");
    return selectedSupplier.supplier_id
      ? `${selectedSupplier.supplier_id} - ${selectedSupplier.name}`
      : selectedSupplier.name;
  };

  const filteredSuppliers = suppliers.filter((supplier) => {
    const query = String(supplierPicker.query || "").trim().toLowerCase();
    if (!query) return true;
    const supplierId = String(supplier.supplier_id || "").toLowerCase();
    const supplierName = String(supplier.name || "").toLowerCase();
    return supplierId.includes(query) || supplierName.includes(query);
  });

  const applyItemToLine = (index, item) => {
    // Keep the selected item and all its packaging fields in one state update.
    updateLine(index, {
      item_id: item.id,
      unit_id: item.unit_id || "",
      pack_size: item.pack_size || 1,
      subunit_id: item.subunit_id || "",
    });

    const previewSupplierId = Number(itemPricePreviews.prices[item.id]?.supplier_id || 0);
    const previewSupplier = suppliers.find((supplier) => Number(supplier.id) === previewSupplierId && itemMatchesSupplier(item, supplier));
    const firstSupplierId = previewSupplier?.id || item.item_suppliers?.map((relation) =>
      suppliers.find((supplier) => itemSupplierMatches(relation, supplier))).find(Boolean)?.id || null;
    if (firstSupplierId && !form.supplier_id) {
      handleChange({
        target: { name: "supplier_id", value: firstSupplierId },
      });
    }

    window.setTimeout(() => autoSaveLine(index), 120);
  };

  const openSupplierPicker = () => {
    if (isMobile) return;
    setSupplierPicker({ open: true, query: "" });
  };

  const closeSupplierPicker = () => {
    setSupplierPicker({ open: false, query: "" });
  };

  const selectSupplier = (supplier) => {
    handleChange({
      target: { name: "supplier_id", value: String(supplier.id) },
    });
    closeSupplierPicker();
  };

  const openItemPicker = (lineIndex) => {
    setItemPicker({ lineIndex, query: "" });
  };

  const closeItemPicker = () => {
    setItemPicker({ lineIndex: null, query: "" });
  };

  const selectItemForLine = (lineIndex, item) => {
    applyItemToLine(lineIndex, item);
    closeItemPicker();
  };

  const handleDeleteDraft = async () => {
    if (!canDeleteOrder || !form.id) return;

    setDeletingDraft(true);
    setError("");

    try {
      const deletedOrderCode = form.order_code;
      await axios.delete(`/api/purchasing/orders/${form.id}`);
      setDeleteConfirmOpen(false);
      resetForm();

      if (typeof onDeleted === "function") {
        onDeleted({ orderCode: deletedOrderCode });
      } else {
        onClose();
      }
    } catch (err) {
      console.error("❌ [PO][Delete] Error:", err);
      setError(err?.response?.data?.error || t("Error deleting the order. Check console for details."));
      setDeleteConfirmOpen(false);
    } finally {
      setDeletingDraft(false);
    }
  };




  const selectedSupplier = suppliers.find((supplier) => String(supplier.id) === String(form.supplier_id)) || form.supplier_id;
  const minimumLabel = supplierMinimumLabel(selectedSupplier, form.currency, languageId);
  const visibleItems = form.supplier_id
  ? items.filter((item) => itemMatchesSupplier(item, selectedSupplier))
  : items;

  const filteredItemPickerItems = visibleItems.filter((item) => {
    const query = String(itemPicker.query || "").trim().toLowerCase();
    if (!query) return true;
    return (
      String(item.code || "").toLowerCase().includes(query) ||
      String(item.name || "").toLowerCase().includes(query)
    );
  });

  useEffect(() => setItemPickerPage(1), [itemPicker.lineIndex, itemPicker.query, form.supplier_id, businessUnitId, form.order_date]);
  const itemPickerPageCount = Math.max(1, Math.ceil(filteredItemPickerItems.length / PURCHASE_PRICE_PAGE_SIZE));
  const currentItemPickerPage = Math.min(itemPickerPage, itemPickerPageCount);
  const pagedItemPickerItems = filteredItemPickerItems.slice((currentItemPickerPage - 1) * PURCHASE_PRICE_PAGE_SIZE, currentItemPickerPage * PURCHASE_PRICE_PAGE_SIZE);
  const itemPricePreviews = usePurchasePricePreviews({
    items: pagedItemPickerItems, businessUnitId, supplierId: form.supplier_id, date: form.order_date,
    currency: form.currency || "EUR", enabled: isOpen && canCreateOrder && itemPicker.lineIndex !== null,
  });


  // 🔹 Reset total
  const resetForm = () => {
    setForm({
      order_code: "",
      type: "purchase",
      supplier_id: "",
      order_date: localDate(),
      expected_delivery_date: "",
      destination_location_id: "",
      notes: "",
      lines: [],
    });

    
    setError("");
    setSaving(false);
    setShowItemPopup(false);
    setPendingLineIndex(null);
    setItemPicker({ lineIndex: null, query: "" });
    setSupplierPicker({ open: false, query: "" });
    setDeleteConfirmOpen(false);
    setDeletingDraft(false);
  };

  // 🔹 Cierre controlado
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // 🚫 Estilos locales: ocultar flechas en inputs numéricos
  const numberInputStyle = `
    input[type=number]::-webkit-inner-spin-button,
    input[type=number]::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    input[type=number] {
      -moz-appearance: textfield;
    }
  `;

  const popupCss = `
    .opq-wrap{position:fixed;inset:0;z-index:9999;background:var(--pu-overlay);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:16px}
    .opq-modal{width:min(1460px,98vw);max-height:calc(100vh - 24px);overflow:auto;border-radius:24px;border:1px solid var(--pu-border);background:var(--pu-surface);color:var(--pu-text);box-shadow:var(--pu-shadow)}
    .opq-modal > h5.mb-3.fw-bold{display:none}
    .opq-head{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;padding:22px 24px 14px;border-bottom:1px solid var(--pu-border)}
    .opq-headMain{flex:1;min-width:0}
    .opq-headActions{display:flex;align-items:center;gap:10px;flex-shrink:0}
    .opq-title{margin:0;font-size:clamp(1.25rem,2vw,1.75rem);font-weight:900;line-height:1.05}
    .opq-sub{margin-top:8px;font-size:.9rem;color:var(--pu-muted)}
    .opq-badge{padding:6px 11px;border-radius:999px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text);font-size:.72rem;font-weight:800;letter-spacing:.08em}
    .opq-closeBtn{width:40px;min-width:40px;height:40px;border-radius:14px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text);font-size:.92rem;font-weight:800;display:inline-flex;align-items:center;justify-content:center;padding:0;cursor:pointer;transition:transform 120ms ease,border-color 120ms ease,background 120ms ease,box-shadow 120ms ease}
    .opq-closeBtn:hover{transform:none;background:var(--pu-soft);border-color:var(--pu-border);box-shadow:none}
    .opq-body{padding:16px 24px 24px}
    .opq-section{border:1px solid var(--pu-border);border-radius:20px;padding:18px;background:var(--pu-soft);box-shadow:none}
    .opq-section + .opq-section{margin-top:16px}
    .opq-sectionTitle{margin:0 0 4px;font-size:1rem;font-weight:850}
    .opq-sectionSub{margin:0 0 14px;color:var(--pu-muted);font-size:.82rem}
    .opq-row{display:grid;gap:16px}
    .opq-row--head{grid-template-columns:minmax(0,1.1fr) minmax(180px,.8fr) minmax(260px,1.3fr)}
    .opq-row--meta{grid-template-columns:minmax(180px,.8fr) minmax(180px,.8fr) minmax(220px,.7fr) minmax(0,1.7fr)}
    .opq-field input,.opq-field select{width:100%}
    .opq-modal .form-label{display:block;margin-bottom:6px;color:var(--pu-muted);font-size:.72rem;text-transform:uppercase;font-weight:700;letter-spacing:.05em}
    .opq-modal .form-control,.opq-modal .form-select{border-radius:12px;border:1px solid var(--pu-border);background:var(--pu-surface)!important;color:var(--pu-text)!important;padding:10px 12px;min-height:42px}
    .opq-modal .form-control::placeholder{color:var(--pu-text)}
    .opq-modal .bg-secondary.form-control,.opq-modal .bg-secondary.form-select{background:var(--pu-surface)!important;border-color:var(--pu-border)!important}
    .opq-modal .form-select option{background:var(--pu-surface);color:var(--pu-text)}
    .opq-linesTop{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin:16px 0 12px}
    .opq-linesTop h6{margin:0;font-size:1rem;font-weight:850}
    .opq-linesTop p{margin:6px 0 0;color:var(--pu-muted);font-size:.8rem}
    .opq-btn{border-radius:12px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text);padding:8px 13px;font-size:.86rem;font-weight:750;cursor:pointer;transition:transform 120ms ease,border-color 120ms ease,background 120ms ease,box-shadow 120ms ease}
    .opq-btn:hover{transform:none;background:var(--pu-soft);box-shadow:none}
    .opq-btn:disabled{opacity:1;cursor:not-allowed;transform:none;box-shadow:none}
    .opq-btn--primary{border-color:var(--pu-border);background:#223a7a;color:#ffffff}
    .opq-btn--danger{border-color:var(--pu-danger);color:var(--pu-danger);background:var(--pu-danger-bg)}
    .opq-btn--cyan{border-color:var(--pu-border);color:var(--pu-text);background:var(--pu-soft)}
    .opq-pickerTrigger{width:100%;justify-content:space-between;text-align:left;display:flex;align-items:center;gap:12px}
    .opq-pickerTrigger span:first-child{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
    .opq-pickerTrigger span:last-child{flex-shrink:0;font-size:.72rem;letter-spacing:.04em;text-transform:uppercase;color:var(--pu-text)}
    .opq-pickerWrap{position:fixed;inset:0;z-index:10040;background:var(--pu-overlay);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:18px}
    .opq-pickerCard{width:min(640px,94vw);max-height:min(78vh,760px);overflow:auto;border-radius:24px;border:1px solid var(--pu-border);background:var(--pu-surface);box-shadow:var(--pu-shadow)}
    .opq-pickerCard--wide{width:min(760px,94vw)}
    .opq-pickerHead{display:flex;justify-content:space-between;gap:14px;align-items:flex-start;padding:18px 18px 12px;border-bottom:1px solid var(--pu-border)}
    .opq-pickerHead h6{margin:0;font-size:1rem;font-weight:850}
    .opq-pickerHead p{margin:6px 0 0;color:var(--pu-muted);font-size:.82rem}
    .opq-pickerBody{padding:14px 18px 18px}
    .opq-pickerSearch{margin-bottom:12px}
    .opq-pickerList{display:grid;gap:10px}
    .opq-pickerActions{display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap;margin-top:16px}
    .opq-pickerItem{width:100%;text-align:left;border-radius:16px;border:1px solid var(--pu-border);background:var(--pu-soft);padding:12px 14px;color:var(--pu-text);transition:transform 120ms ease,border-color 120ms ease,background 120ms ease,box-shadow 120ms ease}
    .opq-pickerItem:hover{transform:none;border-color:var(--pu-border);background:var(--pu-soft);box-shadow:none}
    .opq-pickerItemTitle{display:block;font-size:.95rem;font-weight:800}
    .opq-pickerItemMeta{display:block;margin-top:4px;font-size:.78rem;color:var(--pu-muted)}
    .opq-pickerEmpty{border:1px dashed var(--pu-border);border-radius:16px;padding:16px;text-align:center;color:var(--pu-muted);font-size:.84rem}
    .opq-confirmText{margin:0;color:var(--pu-text);font-size:.92rem;line-height:1.5}
    .opq-confirmText strong{color:var(--pu-text)}
    .opq-tableShell{border:1px solid var(--pu-border);border-radius:22px;padding:14px 16px 16px;background:var(--pu-soft);box-shadow:none}
    .opq-tableWrap{overflow:hidden;max-height:none;border:1px solid var(--pu-border);border-radius:18px;background:var(--pu-soft)}
    .opq-table{width:100%;border-collapse:separate;border-spacing:0;min-width:0;table-layout:fixed;background:var(--pu-soft)!important;border:none!important}
    .opq-table th,.opq-table td{padding:12px 14px;border-bottom:1px solid var(--pu-border);vertical-align:middle}
    .opq-table th{position:sticky;top:0;z-index:2;background:#223a7a;color:#ffffff;font-size:.73rem;text-transform:uppercase;letter-spacing:.06em;font-weight:800}
    .opq-table th:first-child{border-top-left-radius:14px}
    .opq-table th:last-child{border-top-right-radius:14px}
    .opq-table tbody tr{background:var(--pu-soft)}
    .opq-table tbody tr:nth-child(even){background:var(--pu-soft)}
    .opq-table tbody tr:last-child td{border-bottom:none}
    .opq-tableInput{width:100%!important;min-width:0}
    .opq-tableItemCell{display:flex;align-items:center;gap:10px;min-width:0}
    .opq-tableItemMain{flex:1;min-width:0}
    .opq-inlineNew{flex-shrink:0}
    .opq-emptyState{border:1px dashed var(--pu-border);border-radius:20px;padding:22px 20px;background:var(--pu-soft)}
    .opq-emptyState strong{display:block;font-size:1rem}
    .opq-emptyState span{display:block;margin-top:6px;color:var(--pu-muted);font-size:.86rem}
    .opq-mobileLines{display:grid;gap:12px}
    .opq-lineCard{border:1px solid var(--pu-border);border-radius:18px;padding:14px;background:var(--pu-soft)}
    .opq-lineCardTop{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:12px}
    .opq-lineCardTitle{font-size:.84rem;font-weight:800;color:var(--pu-text);text-transform:uppercase;letter-spacing:.05em}
    .opq-lineGrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
    .opq-lineGrid--single{grid-template-columns:minmax(0,1fr)}
    .opq-lineMeta{border-radius:14px;padding:10px 12px;background:var(--pu-soft);border:1px solid var(--pu-border)}
    .opq-lineMetaLabel{display:block;margin-bottom:4px;color:var(--pu-muted);font-size:.66rem;text-transform:uppercase;font-weight:700;letter-spacing:.06em}
    .opq-lineMetaValue{font-size:.92rem;font-weight:700;color:var(--pu-muted)}
    .opq-lineActions{display:flex;justify-content:flex-end;margin-top:12px}
    .opq-selectStack{display:flex;gap:8px;align-items:center}
    .opq-itemPicker{width:100%;text-align:left}
    .opq-alert{margin-top:12px;border:1px solid var(--pu-border);background:var(--pu-danger-bg);color:var(--pu-danger);border-radius:12px;padding:10px 12px;font-size:.84rem}
    .opq-warning{color:var(--pu-text);font-size:.84rem;margin-top:10px}
    .opq-actions{display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap;margin-top:16px}
    @media (max-width:960px){.opq-row--head,.opq-row--meta{grid-template-columns:repeat(2,minmax(0,1fr))}.opq-row--meta .opq-field:last-child{grid-column:1 / -1}}
    @media (max-width:640px){.opq-wrap{padding:8px;align-items:flex-start}.opq-modal{width:100%;max-height:calc(100vh - 16px);border-radius:20px}.opq-head{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:10px;padding:14px 14px 10px}.opq-headMain{min-width:0}.opq-headActions{gap:8px;justify-content:flex-end;align-self:start}.opq-title{font-size:1rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.opq-sub{margin-top:4px;font-size:.74rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.opq-badge{padding:5px 8px;font-size:.64rem}.opq-closeBtn{width:auto;min-width:76px;height:36px;padding:0 12px;border-radius:12px}.opq-body{padding:12px 14px 14px}.opq-section{padding:14px}.opq-row--head,.opq-row--meta,.opq-lineGrid{grid-template-columns:minmax(0,1fr)}.opq-linesTop{flex-direction:column;align-items:stretch}.opq-selectStack{flex-direction:column;align-items:stretch}.opq-actions{flex-direction:column}.opq-actions .opq-btn{width:100%}.opq-pickerWrap{padding:10px;align-items:flex-start}.opq-pickerCard{width:100%;max-height:calc(100vh - 20px);border-radius:20px}.opq-pickerHead{padding:14px 14px 10px}.opq-pickerBody{padding:12px 14px 14px}}
  `;



  if (!isOpen || !canCreateOrder) return null;

  return (
    
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center opq-wrap pilcore-purchasing-overlay pilcore-document-popup" data-theme={themeId}
      style={{ background: "rgba(0,0,0,0.8)", zIndex: 9999 }}
    >
      {/* 🔹 Estilos locales para inputs numéricos */}
      <style>{numberInputStyle}</style>
      <style>{popupCss}</style>

      <div
        className="opq-modal" role="dialog" aria-modal="true" aria-label={t("New Purchase Order")}
        style={{
          width: "96vw",
          maxWidth: 1380,
          maxHeight: "92vh",
          overflowY: "auto",
          border: "1px solid #444",
          borderRadius: "12px",
          padding: 0,
        }}
      >
        <h5 className="mb-3 fw-bold">{t("➕ New Purchase Order")}</h5>

        <div className="opq-head">
          <div className="opq-headMain">
            <h5 className="opq-title">{isMobile ? t("New Order") : t("New Purchase Order")}</h5>
            <div className="opq-sub">
              {form.supplier_id
                ? t("Create and confirm a supplier order with autosaved lines.")
                : t("Choose supplier, build lines and confirm the order when it is ready.")}
            </div>
          </div>
          <div className="opq-headActions">
            <span className="opq-badge">{documentStatus(form.status || "draft", t)}</span>
            <button
              type="button"
              className="opq-closeBtn"
              aria-label={t("Close purchase order popup")}
              title={t("Close")}
              onClick={handleClose}
            >
              {isMobile ? t("Close") : "X"}
            </button>
          </div>
        </div>

        <div className="opq-body">
          <form onSubmit={handleSubmit}>
            <div className="opq-section" ref={linesSectionRef}>
              <h6 className="opq-sectionTitle">{t("Order Setup")}</h6>
              <p className="opq-sectionSub">{t("Keep the header compact, then build and confirm the supplier order from the same draft.")}</p>

              <div className="opq-row opq-row--head">
                <div className="opq-field">
                  <label className="form-label">{t("Order Code")}</label>
                  <input
                    type="text"
                    name="order_code"
                    value={form.order_code}
                    readOnly
                    className="form-control bg-secondary text-white border-0"
                  />
                </div>

                <div className="opq-field">
                  <label className="form-label">{t("Type")}</label>
                  <select
                    name="type"
                    value={form.type}
                    onChange={handleChange}
                    className="form-select bg-black text-white border-secondary"
                  >
                    <option value="purchase">{t("Purchase")}</option>
                    <option value="service">{t("Service")}</option>
                  </select>
                </div>

                <div className="opq-field">
                  <label className="form-label">{t("Supplier *")}</label>
                  {isMobile ? (
                    <select
                      name="supplier_id"
                      value={form.supplier_id}
                      onChange={handleChange}
                      required
                      className="form-select bg-black text-white border-secondary"
                    >
                      <option value="">{t("Select supplier")}</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.supplier_id
                            ? `${supplier.supplier_id} - ${supplier.name}`
                            : supplier.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <button
                      type="button"
                      className="opq-btn opq-pickerTrigger"
                      onClick={openSupplierPicker}
                      aria-haspopup="dialog"
                      aria-expanded={supplierPicker.open ? "true" : "false"}
                    >
                      <span>{getSelectedSupplierLabel()}</span>
                      <span>{supplierPicker.open ? t("Close") : t("Choose")}</span>
                    </button>
                  )}
                  {minimumLabel && <small>{minimumLabel}</small>}
                </div>
              </div>

              <div className="opq-row opq-row--meta" style={{ marginTop: 16 }}>
                <div className="opq-field">
                  <label className="form-label">{t("Order Date")}</label>
                  <input
                    type="date"
                    name="order_date"
                    value={form.order_date}
                    onChange={handleChange}
                    className="form-control bg-black text-white border-secondary"
                  />
                </div>

                <div className="opq-field">
                  <label className="form-label">{t("Expected Delivery")}</label>
                  <input
                    type="date"
                    name="expected_delivery_date"
                    value={form.expected_delivery_date}
                    onChange={handleChange}
                    className="form-control bg-black text-white border-secondary"
                  />
                </div>

                <div className="opq-field">
                  <label className="form-label">{t("Destination Location")}</label>
                  <select
                    name="destination_location_id"
                    value={form.destination_location_id || ""}
                    onChange={handleChange}
                    className="form-select bg-black text-white border-secondary"
                  >
                    <option value="">{t("Automatic by item")}</option>
                    {inventoryLocations.map((location) => (
                      <option key={location.id} value={String(location.id)}>
                        {getLocationName(location, languageId)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="opq-field">
                  <label className="form-label">{t("Total (EUR)")}</label>
                  <input
                    type="text"
                    readOnly
                    value={calculateOrderTotal().toFixed(2)}
                    className="form-control bg-secondary text-white fw-bold text-end border-0"
                  />
                </div>

                <div className="opq-field">
                  <label className="form-label">{t("Notes")}</label>
                  <input
                    type="text"
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    className="form-control bg-black text-white border-secondary"
                    placeholder={t("Internal notes...")}
                  />
                </div>
              </div>
            </div>

            <div className="opq-section">
              <div className="opq-linesTop" style={{ marginTop: 0 }}>
                <div>
                  <h6>{t("Order Lines")}</h6>
                  <p>{t("Select supplier first, then build the lines and prices you want to confirm.")}</p>
                </div>
                <button
                  type="button"
                  className="opq-btn opq-btn--cyan"
                  onClick={addLine}
                  disabled={!form.supplier_id}
                >{t("+ Add Line")}</button>
              </div>

              {!form.supplier_id && (
                <div
                  style={{
                    color: "var(--pu-muted)",
                    fontSize: "0.85rem",
                    marginBottom: "8px",
                    marginTop: "-6px",
                  }}
                >{t("Please select a supplier before adding items.")}</div>
              )}

              {form.lines.length ? (
                <div className="opq-tableShell">
                  <div className="opq-tableWrap" ref={tableWrapRef}>
                    <table className="opq-table">
                      <thead>
                        <tr>
                          <th style={{ width: "40%" }}>{t("Item")}</th>
                          <th style={{ width: "10%", textAlign: "center" }}>{t("Qty")}</th>
                          <th style={{ width: "20%" }}>{t("Packaging")}</th>
                          <th style={{ width: "10%", textAlign: "right" }}>{t("Unit EUR")}</th>
                          <th style={{ width: "12%", textAlign: "right" }}>{t("Line Total")}</th>
                          <th style={{ width: "8%", textAlign: "right" }}>{t("Action")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.lines.map((line, i) => (
                          <tr key={i}>
                            <td>
                              <div className="opq-tableItemCell">
                                <div className="opq-tableItemMain">
                                  <button
                                    type="button"
                                    className="opq-btn opq-pickerTrigger opq-itemPicker"
                                    onClick={() => openItemPicker(i)}
                                  >
                                    <span>{getSelectedItemLabel(line)}</span>
                                    <span>{line.item_id ? t("Change") : t("Choose")}</span>
                                  </button>
                                  {renderLineLocation(line, i)}
                                </div>

                                {!line.item_id && (
                                  <button
                                    type="button"
                                    onClick={() => handleCreateItem(i)}
                                    className="opq-btn opq-btn--cyan opq-inlineNew"
                                    title={t("Create new item")}
                                  >{t("+ New")}</button>
                                )}
                              </div>
                            </td>

                            <td style={{ textAlign: "center" }}>
                              <input
                                type="number"
                                step="0.01"
                                value={line.quantity}
                                onChange={(event) => handleLineChange(i, "quantity", event.target.value)}
                                onBlur={() => autoSaveLine(i)}
                                className="form-control bg-black text-white border-secondary text-center opq-tableInput"
                              />
                            </td>

                            <td>
                              <span className="opq-lineMetaValue" style={{ fontSize: ".86rem" }}>
                                {getPackagingLabel(line)}
                              </span>
                            </td>

                            <td style={{ textAlign: "right" }}>
                              <input
                                type="number"
                                step="0.01"
                                value={line.unit_price}
                                readOnly={Boolean(line.tender_id)}
                                onChange={(event) => handleLineChange(i, "unit_price", event.target.value)}
                                onBlur={() => autoSaveLine(i)}
                                className="form-control bg-black text-white border-secondary text-end opq-tableInput"
                              />
                              <TenderPriceLabel line={line} />
                            </td>

                            <td style={{ textAlign: "right" }}>
                              <span className="opq-lineMetaValue" style={{ fontSize: ".86rem" }}>
                                {getLineTotal(line)}
                              </span>
                            </td>

                            <td style={{ textAlign: "right" }}>
                              <button
                                type="button"
                                className="opq-btn opq-btn--danger"
                                onClick={() => removeLine(i)}
                              >{t("Remove")}</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="opq-emptyState">
                  <strong>{form.supplier_id ? t("No lines yet.") : t("Choose a supplier first.")}</strong>
                  <span>
                    {form.supplier_id
                      ? t("Press Add Line once and the item picker will open immediately.")
                      : t("Once the supplier is selected, Add Line will create the first row and open the item picker.")}
                  </span>
                </div>
              )}
            </div>
          {error && <div className="opq-alert">{error}</div>}

          <p className="opq-muted">{editText("Saving creates a draft for procurement review. It does not submit it for approval or send it to the supplier.")}</p>
          <div className="opq-actions">
          {/* 🔴 Delete Order */}
          {form.id && canDeleteOrder && (
            <button
              type="button"
              className="opq-btn opq-btn--danger"
              onClick={() => setDeleteConfirmOpen(true)}
              disabled={saving || deletingDraft}
            >{t("Delete Draft")}</button>
          )}

          {/* 🟡 Save */}
          <button
            type="submit"
            className="opq-btn opq-btn--primary"
            disabled={saving}
          >
            {saving ? t("Saving...") : editText("Save draft")}
          </button>
        </div>

        </form>
        
        
      </div>
      </div>
      {deleteConfirmOpen && canDeleteOrder && (
        <div
          className="opq-pickerWrap"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !deletingDraft) {
              setDeleteConfirmOpen(false);
            }
          }}
        >
          <div className="opq-pickerCard" onMouseDown={(event) => event.stopPropagation()}>
            <div className="opq-pickerHead">
              <div>
                <h6>{t("Delete Draft Order")}</h6>
                <p>{t("This confirmation now follows the Pilcore modal flow.")}</p>
              </div>
              <button
                type="button"
                className="opq-btn"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deletingDraft}
              >{t("Close")}</button>
            </div>

            <div className="opq-pickerBody">
              <p className="opq-confirmText">{t("Are you sure you want to delete")} <strong>{form.order_code || t("this draft order")}</strong>{t("? This action cannot be undone.")}</p>

              <div className="opq-pickerActions">
                <button
                  type="button"
                  className="opq-btn"
                  onClick={() => setDeleteConfirmOpen(false)}
                  disabled={deletingDraft}
                >{t("Keep Draft")}</button>
                <button
                  type="button"
                  className="opq-btn opq-btn--danger"
                  onClick={handleDeleteDraft}
                  disabled={deletingDraft}
                >
                  {deletingDraft ? t("Deleting...") : t("Delete Draft")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {itemPicker.lineIndex !== null && (
        <div
          className="opq-pickerWrap"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeItemPicker();
          }}
        >
          <div className="opq-pickerCard opq-pickerCard--wide" onMouseDown={(event) => event.stopPropagation()}>
            <div className="opq-pickerHead">
              <div>
                <h6>{t("Select Item")}</h6>
                <p>{t("Search by code or item name...")}</p>
              </div>
              <button type="button" className="opq-btn" onClick={closeItemPicker}>{t("Close")}</button>
            </div>

            <div className="opq-pickerBody">
              <input
                type="search"
                value={itemPicker.query}
                onChange={(event) =>
                  setItemPicker((prev) => ({ ...prev, query: event.target.value }))
                }
                className="form-control opq-pickerSearch"
                placeholder={t("Search by code or item name...")}
                autoFocus
              />

              <div className="opq-pickerList">
                {filteredItemPickerItems.length ? (
                  pagedItemPickerItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="opq-pickerItem" aria-pressed={Number(form.lines[itemPicker.lineIndex]?.item_id) === Number(item.id)}
                      onClick={() => selectItemForLine(itemPicker.lineIndex, item)}
                    >
                      <span className="opq-pickerItemTitle">
                        {item.code ? `${item.code} - ${item.name}` : item.name}
                      </span>
                      <span className="opq-pickerItemMeta">{t("Packaging:")} {getPackagingLabel(item)}{item.reference ? ` | Ref: ${item.reference}` : ""}
                      </span>
                      <PurchasePricePreview price={itemPricePreviews.prices[item.id]} loading={itemPricePreviews.loading}
                        error={itemPricePreviews.error} item={item} units={units} languageId={languageId} />
                    </button>
                  ))
                ) : (
                  <div className="opq-pickerEmpty">{t("No items match your search.")}</div>
                )}
              </div>
              <PurchasePricePagination page={currentItemPickerPage} pageCount={itemPickerPageCount} onPageChange={setItemPickerPage}
                languageId={languageId} total={filteredItemPickerItems.length} />
              {itemPricePreviews.error && <button type="button" className="opq-btn" onClick={itemPricePreviews.retry}>
                {purchasePriceText(languageId).retry}
              </button>}
              {form.lines[itemPicker.lineIndex]?.item_id && <button type="button" className="opq-btn" onClick={() => {
                updateLine(itemPicker.lineIndex, { item_id: "", unit_id: "", pack_size: 1, subunit_id: "" });
                closeItemPicker();
              }}>{String(languageId).startsWith("es") ? "Quitar selección" : String(languageId).startsWith("ca") ? "Esborra la selecció" : "Clear selection"}</button>}
            </div>
          </div>
        </div>
      )}
      {!isMobile && supplierPicker.open && (
        <div
          className="opq-pickerWrap"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeSupplierPicker();
          }}
        >
          <div className="opq-pickerCard" onMouseDown={(event) => event.stopPropagation()}>
            <div className="opq-pickerHead">
              <div>
                <h6>{t("Select Supplier")}</h6>
                <p>{t("Choose the supplier for this order draft with the same Pilcore picker flow.")}</p>
              </div>
              <button type="button" className="opq-btn" onClick={closeSupplierPicker}>{t("Close")}</button>
            </div>

            <div className="opq-pickerBody">
              <input
                type="search"
                value={supplierPicker.query}
                onChange={(event) =>
                  setSupplierPicker((prev) => ({ ...prev, query: event.target.value }))
                }
                className="form-control opq-pickerSearch"
                placeholder={t("Search supplier by id or name...")}
                autoFocus
              />

              <div className="opq-pickerList">
                {filteredSuppliers.length ? (
                  filteredSuppliers.map((supplier) => (
                    <button
                      key={supplier.id}
                      type="button"
                      className="opq-pickerItem" aria-pressed={Number(form.supplier_id) === Number(supplier.id)}
                      onClick={() => selectSupplier(supplier)}
                    >
                      <span className="opq-pickerItemTitle">
                        {supplier.supplier_id
                          ? `${supplier.supplier_id} - ${supplier.name}`
                          : supplier.name}
                      </span>
                      <span className="opq-pickerItemMeta">
                        {supplier.tax_id || t("Use this supplier for the order")}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="opq-pickerEmpty">{t("No suppliers match your search.")}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showItemPopup && (
      <AddItemPopup
        isOpen={showItemPopup}
        supplierIdLock={form.supplier_id}
        context="requisition"
        onClose={() => setShowItemPopup(false)}
        onAdded={(newItem) => {
          handleItemAdded(newItem);
          setShowItemPopup(false);
        }}
      />
    )}

      {dialog}
    </div>
  );
}
